import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  createFileRoute,
  Link,
  Outlet,
  redirect,
  useNavigate,
  useRouterState,
} from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import {
  LayoutDashboard,
  Users,
  FileText,
  DollarSign,
  CreditCard,
  Bell,
  Settings,
  Building,
  AlertTriangle,
  Briefcase,
  ClipboardList,
  ChevronLeft,
  ChevronRight,
  Search,
  LogOut,
  Sun,
  Moon,
  Languages,
  Menu,
  User,
  Shield,
  Lock,
  X,
  Package,
  Warehouse,
  Truck,
  ClipboardCheck,
  Globe,
  Layers,
  ShoppingBag,
  Headphones,
  MessageSquare,
  Plug,
} from '@/lib/icons';

import { LOCAL_STORAGE_KEYS } from '@/constants';
import { useIsMobile } from '@/hooks/use-mobile';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import type { AuthUser } from '@/features/auth/auth';
import { setUser, clearUser } from '@/features/auth/auth-slice';
import { getCurrentUser } from '@/features/auth/api';
import { canAccessRoute, FORBIDDEN_PATH } from '@/lib/auth/route-access';
import type { TenantProducts } from '@/lib/auth/product-scopes';
import { hasPermission } from '@/lib/auth/permissions';
import { store } from '@/store/store';
import { canAccessSystemSection, isSuperAdmin } from '@/lib/auth/roles';
import {
  setSelectedTenantId,
  setTenantSettings,
} from '@/features/tenants/tenant-slice';
import { getTenantSettings } from '@/features/tenants/api';
import { tokenRefreshService } from '@/lib/auth/token-refresh';
import { useAuth } from '@/hooks/useAuth';
import { TenantSelector } from '@/components/common/TenantSelector';
import { BrandLockup } from '@/components/branding/BrandLockup';
import { Button } from '@/components/ui/button';
import { OfflineIndicator } from '@/components/feedback/OfflineIndicator';
import { ErrorBoundary } from '@/components/feedback/ErrorBoundary';
import { SmartBreadcrumb } from '@/components/ui/smart-breadcrumb';
import { GlobalSearchCommand } from '@/components/ui/global-search-command';
import { GlobalSearchProvider } from '@/hooks/useGlobalSearch';
import { useGlobalSearch } from '@/hooks/useGlobalSearch';
import { cn } from '@/lib/utils';
import { getNotifications } from '@/features/notifications/api';
import { getSupportTicketStats } from '@/features/support/api';
import { useOdooStatus } from '@/features/odoo/queries';
import { NotificationModel } from '@/features/notifications/notification';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

export const Route = createFileRoute('/_layout')({
  beforeLoad: ({ location }) => {
    const token = localStorage.getItem(LOCAL_STORAGE_KEYS.ACCESS_TOKEN);
    const userJson = localStorage.getItem(LOCAL_STORAGE_KEYS.USER);
    if (!token || !userJson) {
      throw redirect({ to: '/login' });
    }

    let user: AuthUser | null = null;
    try {
      user = JSON.parse(userJson) as AuthUser;
    } catch {
      throw redirect({ to: '/login' });
    }

    // Refused routes land on an explanation, not on the dashboard. Bouncing
    // silently reads as a broken link — that is how enrolment was reported: a
    // branch manager tapped "Enrol units", arrived at the dashboard, and had
    // no way to tell a permission boundary from a bug.
    //
    // Read from the store rather than the branding context: this runs outside
    // React. `null` until BrandingProvider publishes the resolve, which leaves
    // product-gated routes open on a cold deep link instead of bouncing them.
    const enabledProducts = store.getState().tenant.enabledProducts;

    if (
      location.pathname !== FORBIDDEN_PATH &&
      !canAccessRoute(user, location.pathname, enabledProducts)
    ) {
      throw redirect({
        to: FORBIDDEN_PATH,
        search: { from: location.pathname },
        replace: true,
      });
    }
  },
  component: LayoutComponent,
});

interface NavItem {
  icon: React.ReactNode;
  label: string;
  path: string;
  i18nKey?: string;
  badge?: number;
  badgeTone?: 'destructive';
}

interface NavGroup {
  title: string;
  items: NavItem[];
}

const getNavGroups = (
  t: (key: string) => string,
  user: AuthUser | null | undefined,
  enabledProducts: TenantProducts
): NavGroup[] => {
  const can = (
    scope: string,
    action: 'read' | 'create' | 'update' | 'delete' = 'read'
  ) => hasPermission(user, scope, action);

  const allGroups: NavGroup[] = [
    {
      title: 'Business',
      items: [
        ...(can('dashboard', 'read')
          ? [
              {
                icon: <LayoutDashboard className="h-4 w-4" />,
                label: t('nav.dashboard'),
                path: '/',
              },
            ]
          : []),
        ...(can('contracts', 'read')
          ? [
              {
                icon: <Briefcase className="h-4 w-4" />,
                label: t('nav.portfolio'),
                path: '/portfolio',
              },
            ]
          : []),
        ...(can('applications', 'read')
          ? [
              {
                icon: <ClipboardList className="h-4 w-4" />,
                label: t('nav.applications'),
                path: '/applications',
              },
            ]
          : []),
        ...(can('customers', 'read')
          ? [
              {
                icon: <Users className="h-4 w-4" />,
                label: t('nav.customers'),
                path: '/customers',
              },
            ]
          : []),
        // Merchant credit. Shown only to a tenant entitled to
        // `pay_in_installments` — the product check rides on the route-guard
        // pass below, keyed on the `merchants` scope. See lib/auth/product-scopes.
        ...(can('merchants', 'read')
          ? [
              {
                icon: <ShoppingBag className="h-4 w-4" />,
                label: t('nav.merchants'),
                path: '/merchants',
              },
            ]
          : []),
        ...(can('device_models', 'read')
          ? [
              {
                icon: <Package className="h-4 w-4" />,
                label: t('nav.deviceModels'),
                path: '/device-models',
              },
            ]
          : []),
        ...(can('device_units', 'read')
          ? [
              {
                icon: <Warehouse className="h-4 w-4" />,
                label: t('nav.inventory'),
                path: '/inventory',
              },
              // Its own entry rather than a child of Inventory: branch-to-branch
              // stock movement is a daily receiving task with its own queue, and
              // reaching it only through a "View all" link on the dashboard card
              // meant a receiving clerk had no way to find it. `isActive` above
              // already prefers the longer path, so this does not double-light
              // with Inventory.
              {
                icon: <Truck className="h-4 w-4" />,
                label: t('nav.transfers'),
                path: '/inventory/transfers',
              },
            ]
          : []),
        ...(can('contracts', 'read')
          ? [
              {
                icon: <FileText className="h-4 w-4" />,
                label: t('nav.contracts'),
                path: '/contracts',
              },
            ]
          : []),
        ...(can('payments', 'read')
          ? [
              {
                icon: <DollarSign className="h-4 w-4" />,
                label: t('nav.payments'),
                path: '/payments',
              },
            ]
          : []),
        ...(can('financing_plans', 'read')
          ? [
              {
                icon: <CreditCard className="h-4 w-4" />,
                label: t('nav.financingPlans'),
                path: '/financing-plans',
              },
            ]
          : []),
        ...(can('payments', 'update')
          ? [
              {
                icon: <AlertTriangle className="h-4 w-4" />,
                label: 'Collections',
                path: '/collections',
              },
            ]
          : []),
        // Support lives in Operations, NOT the Tenant section: that section is
        // TenantAdmin-only, and backlog §2/§6 put Agents and Branch Managers on
        // the support workflow. Gated on its own `support` scope.
        ...(can('support', 'read')
          ? [
              {
                icon: <Headphones className="h-4 w-4" />,
                label: 'Support',
                path: '/support',
              },
            ]
          : []),
        // Handset feedback sits beside Support, not in the Tenant section:
        // it is the other half of the same job, and the people who work
        // tickets are the people who work complaints. Gated on its own
        // `feedback` scope so it can be granted independently.
        ...(can('feedback', 'read')
          ? [
              {
                icon: <MessageSquare className="h-4 w-4" />,
                label: t('nav.feedback'),
                path: '/feedback',
              },
            ]
          : []),
      ],
    },
    // The "Tenant" section groups tenant-scoped administration (users, roles,
    // branches, notifications, settings, lock-engine, audit logs). Visibility
    // is restricted to TenantAdmin and the global SuperAdmin via
    // canAccessSystemSection; per-item permission checks below decide which
    // entries appear within. Direct URL access is blocked by route-access.ts.
    // Cross-tenant administration (the Tenants list, products) lives in the
    // Platform group below.
    ...(canAccessSystemSection(user)
      ? [
          {
            title: 'Tenant',
            items: [
              ...(can('lock_engine', 'read')
                ? [
                    {
                      icon: <Lock className="h-4 w-4" />,
                      label: 'Lock Engine',
                      path: '/lock-engine',
                    },
                  ]
                : []),
              ...(can('audit_logs', 'read')
                ? [
                    {
                      icon: <ClipboardCheck className="h-4 w-4" />,
                      label: 'Audit Logs',
                      path: '/audit-logs',
                    },
                  ]
                : []),
              ...(can('notifications', 'read')
                ? [
                    {
                      icon: <Bell className="h-4 w-4" />,
                      label: t('nav.notifications'),
                      path: '/notifications',
                    },
                  ]
                : []),
              ...(can('users', 'read')
                ? [
                    {
                      icon: <Shield className="h-4 w-4" />,
                      label: 'Users',
                      path: '/users',
                    },
                  ]
                : []),
              ...(can('tenant_settings', 'read')
                ? [
                    {
                      icon: <Settings className="h-4 w-4" />,
                      label: t('nav.tenant'),
                      path: '/settings',
                    },
                  ]
                : []),
              ...(can('branches', 'read')
                ? [
                    {
                      icon: <Building className="h-4 w-4" />,
                      label: t('nav.branches'),
                      path: '/settings/branches',
                    },
                  ]
                : []),
              ...(can('tenant_settings', 'read')
                ? [
                    {
                      icon: <Plug className="h-4 w-4" />,
                      label: t('nav.odoo'),
                      path: '/settings/odoo',
                    },
                  ]
                : []),
            ],
          },
        ]
      : []),
    // ── Platform ─── Super Admin only
    ...(isSuperAdmin(user)
      ? [
          {
            title: 'Platform',
            items: [
              {
                icon: <Globe className="h-4 w-4" />,
                label: 'Tenants',
                path: '/settings/platform/tenants',
              },
              {
                icon: <Layers className="h-4 w-4" />,
                label: 'Products',
                path: '/settings/platform/products',
              },
            ],
          },
        ]
      : []),
  ];

  // Final pass through the route guard: an item that would bounce on click
  // has no business being in the menu, whatever its own check said.
  return allGroups
    .map((g) => ({
      ...g,
      items: g.items.filter((i) =>
        canAccessRoute(user, i.path, enabledProducts)
      ),
    }))
    .filter((g) => g.items.length > 0);
};

function AppSidebar({
  collapsed,
  onToggle,
}: {
  collapsed: boolean;
  onToggle: () => void;
}) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { userInitials, userDisplayName, user, logout } = useAuth();
  const currentPath = useRouterState({
    select: (state) => state.location.pathname,
  });
  const enabledProducts = useAppSelector((s) => s.tenant.enabledProducts);

  const navGroups = getNavGroups(t, user, enabledProducts);

  // Notifications live in the admin-only System section, so only poll for the
  // unread badge when the user can actually reach that section.
  const { data: notifData } = useQuery({
    queryKey: ['notifications'],
    queryFn: () => getNotifications(),
    staleTime: 30_000,
    refetchInterval: 30_000,
    enabled: canAccessSystemSection(user),
  });
  const unreadCount = (notifData?.value ?? []).filter(
    (n: NotificationModel) => !n.isRead
  ).length;

  // Open support tickets, polled on the same cadence. Only fetched for users
  // who hold `support:read` — otherwise every Agent without the scope would
  // poll an endpoint that can only 403.
  const canReadSupport = hasPermission(user, 'support', 'read');
  const { data: supportStats } = useQuery({
    queryKey: ['support-ticket-stats'],
    queryFn: () => getSupportTicketStats(),
    staleTime: 30_000,
    refetchInterval: 30_000,
    enabled: canReadSupport,
  });
  // The badge counts what needs picking up, not everything unresolved: a
  // ticket already assigned and in progress is somebody's current work, and
  // surfacing it as outstanding makes the number permanently non-zero.
  const openTicketCount = supportStats?.succeeded
    ? (supportStats.value?.open ?? 0)
    : 0;

  // Rows Odoo gave up on are the one Odoo state an operator has to act on, so
  // they get a count here; polled with the status the Odoo screens share.
  const { data: odooStatus } = useOdooStatus({
    poll: true,
    enabled: canAccessSystemSection(user),
  });
  const odooAbandoned = odooStatus?.counts.abandoned ?? 0;

  const navGroupsWithBadge = useMemo(
    () =>
      navGroups.map((group) => ({
        ...group,
        items: group.items.map((item) => {
          if (item.path === '/notifications') {
            return { ...item, badge: unreadCount };
          }
          if (item.path === '/support') {
            return { ...item, badge: openTicketCount };
          }
          if (item.path === '/settings/odoo') {
            return {
              ...item,
              badge: odooAbandoned,
              badgeTone: 'destructive' as const,
            };
          }
          return item;
        }),
      })),
    [navGroups, unreadCount, openTicketCount, odooAbandoned]
  );

  const allNavPaths = navGroupsWithBadge.flatMap((g) =>
    g.items.map((i) => i.path)
  );

  const isActive = (path: string) => {
    if (path === '/') return currentPath === '/';
    if (currentPath === path) return true;
    if (currentPath.startsWith(path + '/') || currentPath.startsWith(path)) {
      const hasMoreSpecificMatch = allNavPaths.some(
        (p) =>
          p !== path &&
          p.length > path.length &&
          p.startsWith(path) &&
          currentPath.startsWith(p)
      );
      return !hasMoreSpecificMatch;
    }
    return false;
  };

  const showTenantSelector =
    isSuperAdmin(user) && !user?.tenantId;

  return (
    <aside
      className={cn(
        'fixed inset-y-0 left-0 z-50 flex flex-col bg-[hsl(var(--sidebar-background))] border-r border-sidebar-border transition-all duration-300 ease-in-out',
        collapsed ? 'w-[68px]' : 'w-[232px]'
      )}
    >
      <div
        className={cn(
          'flex h-16 items-center px-4 shrink-0',
          collapsed ? 'justify-center' : 'justify-between'
        )}
      >
        {!collapsed && (
          <Link to="/" className="flex items-center group">
            <BrandLockup
              size="md"
              className="transition-transform duration-200 group-hover:scale-105"
            />
          </Link>
        )}
        {collapsed && (
          <Link to="/" className="flex h-9 w-9 items-center justify-center">
            <BrandLockup markOnly size="md" className="rounded-lg" />
          </Link>
        )}
      </div>

      {showTenantSelector && <TenantSelector collapsed={collapsed} />}

      <nav className="flex-1 overflow-y-auto overflow-x-hidden py-3 px-2.5">
        <TooltipProvider delayDuration={0}>
          {navGroupsWithBadge.map((group, gi) => (
            <div key={gi} className={cn(gi > 0 && 'mt-5')}>
              {group.title && !collapsed && (
                <div className="px-2.5 mb-1.5">
                  <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-sidebar-muted-foreground">
                    {group.title}
                  </span>
                </div>
              )}
              {group.title && collapsed && (
                <div className="mx-auto my-3 h-px w-6 bg-sidebar-border" />
              )}
              <div className="space-y-0.5">
                {group.items.map((item) => {
                  const active = isActive(item.path);
                  const button = (
                    <button
                      key={item.path}
                      onClick={() => navigate({ to: item.path })}
                      className={cn(
                        'group relative flex items-center w-full rounded-md text-[13px] font-medium transition-colors duration-150',
                        collapsed ? 'h-9 justify-center' : 'h-9 gap-2.5 px-2.5',
                        active
                          ? 'bg-sidebar-accent text-sidebar-accent-foreground'
                          : 'text-sidebar-muted-foreground hover:bg-sidebar-muted hover:text-sidebar-foreground'
                      )}
                    >
                      <span
                        className={cn(
                          'relative shrink-0 transition-colors duration-150',
                          active
                            ? 'text-sidebar-accent-foreground'
                            : 'text-sidebar-muted-foreground group-hover:text-sidebar-foreground'
                        )}
                      >
                        {item.icon}
                        {collapsed && item.badge != null && item.badge > 0 && (
                          <span
                            className={cn(
                              'absolute -top-1.5 -right-1.5 flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[9px] font-bold leading-none ring-2 ring-[hsl(var(--sidebar-background))]',
                              item.badgeTone === 'destructive'
                                ? 'bg-destructive text-destructive-foreground'
                                : 'bg-sidebar-primary text-sidebar-primary-foreground'
                            )}
                          >
                            {item.badge > 9 ? '9+' : item.badge}
                          </span>
                        )}
                      </span>
                      {!collapsed && (
                        <span className="truncate">{item.label}</span>
                      )}
                      {!collapsed && item.badge != null && item.badge > 0 && (
                        <span
                          className={cn(
                            'ml-auto flex h-[18px] min-w-[18px] items-center justify-center rounded-full px-1.5 text-[10px] font-semibold tabular-nums',
                            item.badgeTone === 'destructive'
                              ? 'bg-destructive text-destructive-foreground'
                              : active
                                ? 'bg-white/70 text-sidebar-accent-foreground'
                                : 'bg-warning/15 text-warning'
                          )}
                        >
                          {item.badge > 99 ? '99+' : item.badge}
                        </span>
                      )}
                    </button>
                  );

                  if (collapsed) {
                    return (
                      <Tooltip key={item.path}>
                        <TooltipTrigger asChild>{button}</TooltipTrigger>
                        <TooltipContent
                          side="right"
                          sideOffset={8}
                          className="font-medium"
                        >
                          {item.label}
                        </TooltipContent>
                      </Tooltip>
                    );
                  }
                  return button;
                })}
              </div>
            </div>
          ))}
        </TooltipProvider>
      </nav>

      <div className="px-2.5 py-2">
        <button
          onClick={onToggle}
          className={cn(
            'flex items-center w-full rounded-lg h-9 text-xs font-medium text-sidebar-muted-foreground hover:bg-sidebar-accent/50 hover:text-sidebar-foreground transition-all duration-200',
            collapsed ? 'justify-center' : 'gap-2 px-3'
          )}
        >
          {collapsed ? (
            <ChevronRight className="h-4 w-4" />
          ) : (
            <ChevronLeft className="h-4 w-4" />
          )}
          {!collapsed && <span>Collapse</span>}
        </button>
      </div>

      <div
        className={cn(
          'px-2.5 py-3 border-t border-sidebar-border shrink-0',
          collapsed ? 'flex justify-center' : ''
        )}
      >
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className={cn(
                'flex items-center gap-2.5 rounded-lg transition-all duration-150 hover:bg-sidebar-muted w-full',
                collapsed ? 'h-10 w-10 justify-center p-0' : 'h-12 px-2'
              )}
            >
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[hsl(87_60%_94%)] text-[hsl(87_100%_17%)] text-xs font-bold">
                {userInitials}
              </div>
              {!collapsed && (
                <div className="flex flex-col items-start min-w-0">
                  <span className="text-[13px] font-semibold truncate max-w-[150px] text-sidebar-foreground leading-tight">
                    {userDisplayName}
                  </span>
                  <span className="text-[11px] text-sidebar-muted-foreground truncate max-w-[150px] leading-tight">
                    {user?.email ?? user?.phoneNumber ?? ''}
                  </span>
                </div>
              )}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            side={collapsed ? 'right' : 'top'}
            align="start"
            className="w-56"
          >
            <DropdownMenuLabel className="font-normal">
              <div className="flex flex-col space-y-1">
                <p className="text-sm font-medium">{userDisplayName}</p>
                <p className="text-xs text-muted-foreground">
                  {user?.email ?? user?.phoneNumber ?? ''}
                </p>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => navigate({ to: `/users/${user?.id}` })}
            >
              <User className="mr-2 h-4 w-4" />
              Profile
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={logout}
              className="text-destructive focus:text-destructive"
            >
              <LogOut className="mr-2 h-4 w-4" />
              Sign Out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </aside>
  );
}

function MobileSidebar({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { userInitials, userDisplayName, user, logout } = useAuth();
  const currentPath = useRouterState({
    select: (state) => state.location.pathname,
  });
  const enabledProducts = useAppSelector((s) => s.tenant.enabledProducts);
  const navGroups = getNavGroups(t, user, enabledProducts);

  const allNavPaths = navGroups.flatMap((g) => g.items.map((i) => i.path));

  const isActive = (path: string) => {
    if (path === '/') return currentPath === '/';
    if (currentPath === path) return true;
    if (currentPath.startsWith(path + '/') || currentPath.startsWith(path)) {
      const hasMoreSpecificMatch = allNavPaths.some(
        (p) =>
          p !== path &&
          p.length > path.length &&
          p.startsWith(path) &&
          currentPath.startsWith(p)
      );
      return !hasMoreSpecificMatch;
    }
    return false;
  };

  const handleNav = (path: string) => {
    navigate({ to: path });
    onClose();
  };

  if (!open) return null;

  const showTenantSelector =
    isSuperAdmin(user) && !user?.tenantId;

  return (
    <>
      <div
        className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200"
        onClick={onClose}
      />
      <aside className="fixed inset-y-0 left-0 z-50 w-[280px] bg-[hsl(var(--sidebar-background))] border-r border-sidebar-border shadow-[0_24px_40px_-8px_hsl(225_50%_8%/0.18)] animate-in slide-in-from-left duration-300 flex flex-col">
        <div className="flex h-16 items-center justify-between px-4 shrink-0">
          <Link to="/" onClick={onClose} className="flex items-center">
            <BrandLockup size="md" />
          </Link>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="h-8 w-8 text-sidebar-muted-foreground hover:text-sidebar-foreground hover:bg-sidebar-accent/50"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        {showTenantSelector && <TenantSelector collapsed={false} />}

        <nav className="flex-1 overflow-y-auto py-3 px-2.5">
          {navGroups.map((group, gi) => (
            <div key={gi} className={cn(gi > 0 && 'mt-5')}>
              {group.title && (
                <div className="px-2.5 mb-1.5">
                  <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-sidebar-muted-foreground">
                    {group.title}
                  </span>
                </div>
              )}
              <div className="space-y-0.5">
                {group.items.map((item) => {
                  const active = isActive(item.path);
                  return (
                    <button
                      key={item.path}
                      onClick={() => handleNav(item.path)}
                      className={cn(
                        'group relative flex items-center w-full rounded-md h-10 gap-2.5 px-2.5 text-[13px] font-medium transition-colors duration-150',
                        active
                          ? 'bg-sidebar-accent text-sidebar-accent-foreground'
                          : 'text-sidebar-muted-foreground hover:bg-sidebar-muted hover:text-sidebar-foreground'
                      )}
                    >
                      <span
                        className={cn(
                          'shrink-0',
                          active
                            ? 'text-sidebar-accent-foreground'
                            : 'text-sidebar-muted-foreground group-hover:text-sidebar-foreground'
                        )}
                      >
                        {item.icon}
                      </span>
                      <span className="truncate">{item.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>

        <div className="px-3 py-3 border-t border-sidebar-border shrink-0">
          <div className="flex items-center gap-2.5 p-2 rounded-lg bg-sidebar-muted">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[hsl(87_60%_94%)] text-[hsl(87_100%_17%)] text-sm font-bold">
              {userInitials}
            </div>
            <div className="flex flex-col min-w-0">
              <span className="text-[13px] font-semibold truncate text-sidebar-foreground">
                {userDisplayName}
              </span>
              <span className="text-[11px] text-sidebar-muted-foreground truncate">
                {user?.email ?? user?.phoneNumber ?? ''}
              </span>
            </div>
          </div>
          <button
            onClick={() => {
              logout();
              onClose();
            }}
            className="flex items-center gap-2 w-full mt-2 px-3 h-10 rounded-lg text-sm font-medium text-destructive-foreground/80 hover:bg-destructive/15 transition-colors"
          >
            <LogOut className="h-4 w-4" />
            Sign Out
          </button>
        </div>
      </aside>
    </>
  );
}

function TopBar({ onOpenMobile }: { onOpenMobile: () => void }) {
  const [theme, setTheme] = useState<'light' | 'dark'>('light');
  const isMobile = useIsMobile();
  const { t, i18n } = useTranslation();
  const { setIsOpen } = useGlobalSearch();

  useEffect(() => {
    const savedTheme = localStorage.getItem('theme') as 'light' | 'dark' | null;
    const prefersDark = window.matchMedia(
      '(prefers-color-scheme: dark)'
    ).matches;
    if (savedTheme) {
      setTheme(savedTheme);
      document.documentElement.classList.toggle('dark', savedTheme === 'dark');
    } else if (prefersDark) {
      setTheme('dark');
      document.documentElement.classList.add('dark');
    }
  }, []);

  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
    document.documentElement.classList.toggle('dark');
    localStorage.setItem('theme', newTheme);
  };

  const changeLanguage = async (lng: string) => {
    await i18n.changeLanguage(lng);
    localStorage.setItem('i18nextLng', lng);
  };

  return (
    <header className="sticky top-0 z-30 flex h-14 items-center justify-between bg-card/85 border-b border-border-light px-4 lg:px-6 shrink-0 backdrop-blur supports-[backdrop-filter]:bg-card/70">
      <div className="flex items-center gap-3 min-w-0">
        {isMobile && (
          <Button
            variant="ghost"
            size="icon"
            className="h-9 w-9 lg:hidden text-muted-foreground hover:text-foreground hover:bg-accent"
            onClick={onOpenMobile}
          >
            <Menu className="h-5 w-5" />
          </Button>
        )}
        <SmartBreadcrumb className="hidden min-w-0 sm:flex" />
      </div>

      <div className="flex items-center gap-1">
        <Button
          variant="ghost"
          size="sm"
          className="hidden sm:inline-flex items-center gap-2 text-muted-foreground hover:text-foreground hover:bg-accent h-9 px-3 rounded-lg"
          onClick={() => setIsOpen(true)}
        >
          <Search className="h-4 w-4" />
          <span className="text-sm">{t('common.search')}...</span>
          <kbd className="pointer-events-none inline-flex h-5 select-none items-center gap-1 rounded border border-border-light bg-surface-container-low px-1.5 font-mono text-[10px] font-medium text-muted-foreground">
            <span className="text-xs">Ctrl+</span>K
          </kbd>
        </Button>
        {isMobile && (
          <Button
            variant="ghost"
            size="icon"
            className="h-9 w-9 text-muted-foreground hover:text-foreground hover:bg-accent"
            onClick={() => setIsOpen(true)}
          >
            <Search className="h-5 w-5" />
          </Button>
        )}

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-9 w-9 text-muted-foreground hover:text-foreground hover:bg-accent"
            >
              <Languages className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-44">
            {[
              { code: 'en', label: 'English', flag: '🇺🇸' },
              { code: 'so', label: 'Soomaali', flag: '🇸🇴' },
            ].map((lang) => (
              <DropdownMenuItem
                key={lang.code}
                onClick={() => changeLanguage(lang.code)}
                className={cn(i18n.language === lang.code && 'bg-accent')}
              >
                <span className="mr-2">{lang.flag}</span>
                <span>{lang.label}</span>
                {i18n.language === lang.code && (
                  <span className="ml-auto text-primary text-xs font-bold">
                    ✓
                  </span>
                )}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        <Button
          variant="ghost"
          size="icon"
          className="h-9 w-9 text-muted-foreground hover:text-foreground hover:bg-accent"
          onClick={toggleTheme}
        >
          {theme === 'light' ? (
            <Moon className="h-4 w-4" />
          ) : (
            <Sun className="h-4 w-4" />
          )}
        </Button>
      </div>
    </header>
  );
}

function LayoutComponent() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const selectedTenantId = useAppSelector(
    (state) => state.tenant.selectedTenantId
  );
  const currentUser = useAppSelector((state) => state.auth.user);
  // Bypass users (e.g. SuperAdmin) have no tenant on their JWT; tenant-scoped
  // endpoints reject them with 400 until they pick a tenant via TenantSelector.
  const hasTenantContext =
    !!currentUser &&
    (!isSuperAdmin(currentUser) || !!selectedTenantId);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const storedUser = localStorage.getItem(LOCAL_STORAGE_KEYS.USER);
    if (storedUser) {
      try {
        dispatch(setUser(JSON.parse(storedUser) as AuthUser));
      } catch {
        /* ignore */
      }
    }
    void getCurrentUser()
      .then((result) => {
        if (result.succeeded && result.value) {
          dispatch(setUser(result.value));
          localStorage.setItem(
            LOCAL_STORAGE_KEYS.USER,
            JSON.stringify(result.value)
          );
        }
      })
      .catch(() => {
        /* handled by interceptor */
      });
  }, [dispatch]);

  useEffect(() => {
    const savedTenantId = localStorage.getItem(
      LOCAL_STORAGE_KEYS.SELECTED_TENANT
    );
    if (savedTenantId) {
      dispatch(setSelectedTenantId(savedTenantId));
    }
  }, [dispatch]);

  useEffect(() => {
    if (!hasTenantContext) return;
    getTenantSettings().then((result) => {
      if (result.succeeded && result.value) {
        dispatch(setTenantSettings(result.value));
      }
    });
  }, [dispatch, selectedTenantId, hasTenantContext]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        if (tokenRefreshService.hasExpiredSession()) {
          tokenRefreshService.removeToken();
          dispatch(clearUser());
          navigate({ to: '/login' });
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () =>
      document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [dispatch, navigate]);

  useEffect(() => {
    const saved = localStorage.getItem('sidebar_collapsed');
    if (saved === 'true') setSidebarCollapsed(true);
  }, []);

  const handleToggle = () => {
    const next = !sidebarCollapsed;
    setSidebarCollapsed(next);
    localStorage.setItem('sidebar_collapsed', next.toString());
  };

  return (
    <GlobalSearchProvider>
      <div className="flex min-h-screen w-full bg-background">
        {!isMobile && (
          <AppSidebar collapsed={sidebarCollapsed} onToggle={handleToggle} />
        )}

        {isMobile && (
          <MobileSidebar
            open={mobileOpen}
            onClose={() => setMobileOpen(false)}
          />
        )}

        <div
          className={cn(
            'flex flex-1 flex-col min-h-screen transition-all duration-300',
            !isMobile && (sidebarCollapsed ? 'ml-[68px]' : 'ml-[232px]')
          )}
        >
          <TopBar onOpenMobile={() => setMobileOpen(true)} />

          <main
            className="flex-1 overflow-auto"
            id="main-content"
            tabIndex={-1}
          >
            {/* Generous bottom padding stands in for the removed footer so the
                last card never sits flush against the viewport edge. */}
            <div className="mx-auto w-full max-w-7xl px-4 pt-6 pb-12 md:px-6 md:pt-7 lg:px-7">
              <div className="animate-fade-in">
                <ErrorBoundary>
                  <Outlet />
                </ErrorBoundary>
              </div>
            </div>
          </main>
        </div>

        <OfflineIndicator />
        <GlobalSearchCommand />
      </div>
    </GlobalSearchProvider>
  );
}
